import type { API, BlockAPI } from '@editorjs/editorjs';
import type { KeyboardEventHandler } from 'react';
import type { FlotiqImageTune } from './FlotiqImagePluginSettings';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../../../../lib/flotiq-client/api-helpers';
import {
  getDefaultImageProperties,
  getTestProps,
} from '../../../../../lib/helpers';

import Button from '../../../../Button/Button';

interface FlotiqImageData {
  url: string;
  extension: string;
  width: number | null;
  height: number | null;
  fileName: string;
  type: string;
}

export interface FlotiqImagePluginData
  extends FlotiqImageData, FlotiqImageTune {
  caption: string;
}

export type FlotiqImageOpenModalFn = () => Promise<FlotiqImageData[]>;

export type FlotiqImageSaveDataFn = (data: FlotiqImagePluginData) => void;

interface FlotiqImagePluginBodyProps {
  /**
   * Modal opening callback
   */
  openModal?: FlotiqImageOpenModalFn;
  /**
   * Saving selected image into callback
   */
  saveSelected: FlotiqImageSaveDataFn;
  /**
   * Editor js block object
   */
  block: BlockAPI;
  /**
   * Editor js api object
   */
  api: API;
  /**
   * Selected image data
   */
  data?: FlotiqImageData | FlotiqImagePluginData;
  /**
   * If plugin is disabled
   */
  disabled?: boolean;
  /**
   * Test id for image plugin body
   */
  testId?: string;
}

const formatMediaModalResult = (result: FlotiqImageData[]) => {
  const resultObject = Object.values(result)[0];
  return {
    url: getMediaUrl(resultObject, 0, 0, false),
    extension: resultObject.extension,
    fileName: resultObject.fileName,
    width: resultObject.width,
    height: resultObject.height,
    type: resultObject.type,
  };
};

const isImage = (extension: string) => {
  if (extension) {
    return ['jpg', 'png', 'bmp', 'svg', 'gif', 'webp', 'ico', 'jpeg'].includes(
      extension.toLowerCase(),
    );
  }
};

const FlotiqImagePluginBody = ({
  openModal,
  saveSelected,
  data,
  block,
  api,
  disabled = false,
  testId = '',
}: FlotiqImagePluginBodyProps) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);

  const [selectedImage, setSelectedImage] = useState({
    url: data?.url || '',
    extension: data?.extension || '',
    width: data?.width || null,
    height: data?.height || null,
    fileName: data?.fileName || '',
    type: data?.type || '',
  });

  const tunes = useMemo(
    () => ({
      withBorder: data && 'withBorder' in data ? data.withBorder : false,
      withBackground:
        data && 'withBackground' in data ? data.withBackground : false,
      stretched: data && 'stretched' in data ? data.stretched : false,
    }),
    [data],
  );

  const handleModal = useCallback(async () => {
    const result = await openModal?.();
    if (!result) {
      return;
    }
    setSelectedImage(formatMediaModalResult(result));
  }, [openModal]);

  useEffect(() => {
    if (selectedImage.url) {
      saveSelected({
        ...selectedImage,
        ...tunes,
        ...(!isImage(selectedImage.extension) && { width: null, height: null }),
        caption: captionRef.current?.innerHTML || '',
      });

      block.dispatchChange();
    }
  }, [selectedImage, saveSelected, tunes, block]);

  const imageFileContainer = useMemo(() => {
    if (selectedImage && !isImage(selectedImage.extension)) {
      return (
        <div
          className={twMerge(
            'flex items-center flex-row w-full justify-start p-2',
          )}
          {...getTestProps(testId, 'selected-file')}
        >
          <div
            className="flex items-center justify-center rounded-full
            h-[32px] sm:h-12 min-w-[32px] w-[32px] sm:w-12 bg-blue-300 text-sm sm:text-base"
          >
            {selectedImage.extension || ''}
          </div>
          <a
            href={selectedImage.url}
            target={'_blank'}
            rel="noreferrer"
            className="text-sm sm:text-base break-all ml-3"
          >
            {selectedImage.fileName}
          </a>
        </div>
      );
    }
    return null;
  }, [selectedImage, testId]);

  const handleDeleteBLock = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      if (event.code === 'Backspace') {
        event.stopPropagation();
        event.preventDefault();
        api.blocks.delete(api.blocks.getCurrentBlockIndex());
      }
    },
    [api],
  );

  const handleFocusSwap = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      if (
        event.code === 'Backspace' &&
        (event.target as HTMLDivElement).textContent === ''
      ) {
        event.preventDefault();
        event.stopPropagation();
        imageRef.current?.focus();
      }
    },
    [],
  );

  const selectedImageCard = useMemo(() => {
    return (
      selectedImage.url && (
        <div
          {...getTestProps(testId, 'image-container')}
          ref={imageRef}
          tabIndex={-1}
          onKeyDown={handleDeleteBLock}
          className={twMerge(
            'border border-transparent focus:border-blue',
            tunes.withBorder && 'border border-solid border-slate-400/80',
            tunes.withBackground && 'bg-slate-50',
            tunes.stretched && 'w-full h-full',
          )}
        >
          {!isImage(selectedImage.extension) ? (
            imageFileContainer
          ) : (
            <img
              {...getTestProps(testId, 'selected-image')}
              className={twMerge(
                'w-64 h-full',
                tunes.stretched && 'w-full h-full',
              )}
              src={selectedImage.url}
              alt={selectedImage.fileName}
              {...getDefaultImageProperties()}
            />
          )}
        </div>
      )
    );
  }, [
    selectedImage.url,
    selectedImage.extension,
    selectedImage.fileName,
    testId,
    handleDeleteBLock,
    tunes.withBackground,
    tunes.stretched,
    tunes.withBorder,
    imageFileContainer,
  ]);

  if (!selectedImage.url) {
    return (
      <Button
        onClick={handleModal}
        additionalClasses={'mt-2'}
        {...getTestProps(testId, 'open-modal', 'testId')}
      >
        {t('Global.MediaLibrary')}
      </Button>
    );
  }
  return (
    <div className={'flex flex-col py-3'}>
      {selectedImageCard}
      <div
        ref={captionRef}
        dangerouslySetInnerHTML={{
          __html: data && 'caption' in data ? data.caption : '',
        }}
        className={twMerge(
          'text-base px-4 py-3 w-full dark:bg-transparent',
          'focus:outline-hidden focus:border-blue',
          'border-slate-200 dark:border-slate-700 border rounded-lg',
          'text-slate-900 dark:text-slate-200',
        )}
        onKeyDown={handleFocusSwap}
        data-placeholder="Content"
        contentEditable={!disabled}
        {...getTestProps(testId, 'caption-div')}
      />
    </div>
  );
};

export default FlotiqImagePluginBody;
